import { supabase } from '@/lib/supabase';
import { Vehicle } from '@/utils/types';
import { latestEventDate, passesRecencyWindow } from '@/utils/webVisibility';
import { create } from 'zustand';
import { VEHICLE_INVENTORY_SELECT } from '@/lib/inventory-select';
import { claveInventario, tomarPrefetch } from '@/lib/prefetch-temprano';
import {
  ORDEN_TENANT_POR_DEFECTO,
  ordenInventario,
  type OrdenWebTenant,
} from '@/lib/orden-vehiculos';

export { VEHICLE_INVENTORY_SELECT };

/**
 * Fila del inventario tal como llega de la base, ANTES del filtro de
 * visibilidad del listado. La consumen las secciones del builder (grid,
 * carrusel), que aplican sus propias reglas de estado y ventana: así una sola
 * descarga alimenta al listado y a todas las secciones, sin que ninguna
 * cambie lo que muestra.
 */
export type InventoryRow = Vehicle & {
  vehicles_sales?: Array<{ created_at: string; is_historical_import?: boolean | null }> | null;
  vehicles_reservations?: Array<{ created_at: string }> | null;
  event_date?: string;
};

interface VehiclesStore {
  vehicles: Vehicle[];
  setVehicles: (vehicles: Vehicle[]) => void;
  /** Inventario crudo (todos los estados) tal como vino de la base, ordenado por created_at desc. */
  rawVehicles: InventoryRow[];
  /** Para qué cliente está cargado `rawVehicles` (null = nunca / falló). */
  loadedForClientId: string | null;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  error: string | null;
  fetchVehicles: (clientId: string, hasDemo?: boolean, orden?: OrdenWebTenant) => Promise<void>;
  /**
   * Dispara `fetchVehicles` solo si no hay una carga hecha ni en curso para
   * ese cliente. Es lo que llaman los consumidores: con esto el inventario se
   * baja UNA vez por visita aunque lo pidan el listado, el buscador del home,
   * el formulario y tres secciones del builder a la vez.
   */
  ensureVehicles: (clientId: string | number, hasDemo?: boolean, orden?: OrdenWebTenant) => Promise<void>;
}

// Promesa de la carga en curso, por cliente. Fuera del estado de zustand: es
// plomería, no algo que deba re-renderizar a nadie.
let inFlight: { clientId: string; promise: Promise<void> } | null = null;

const useVehiclesStore = create<VehiclesStore>((set, get) => ({
  vehicles: [],
  setVehicles: (vehicles: Vehicle[]) => set({ vehicles }),
  rawVehicles: [],
  loadedForClientId: null,
  isLoading: true,
  setIsLoading: (isLoading: boolean) => set({ isLoading }),
  error: null,

  ensureVehicles: (clientId: string | number, hasDemo: boolean = false, orden: OrdenWebTenant = ORDEN_TENANT_POR_DEFECTO) => {
    const key = String(clientId);
    if (inFlight && inFlight.clientId === key) return inFlight.promise;
    if (get().loadedForClientId === key) return Promise.resolve();
    const promise = get()
      .fetchVehicles(key, hasDemo, orden)
      .finally(() => {
        if (inFlight && inFlight.promise === promise) inFlight = null;
      });
    inFlight = { clientId: key, promise };
    return promise;
  },

  fetchVehicles: async (clientId: string, hasDemo: boolean = false, orden: OrdenWebTenant = ORDEN_TENANT_POR_DEFECTO) => {
    console.log(
      'Iniciando fetchVehicles con clientId:',
      clientId,
      'hasDemo:',
      hasDemo
    );
    set({ isLoading: true });
    try {
      // Si el `<head>` ya disparó esta misma consulta (ver
      // lib/prefetch-temprano), se usa esa respuesta; si falló o no existe,
      // se pide como siempre.
      let data: any[] | null = null;
      const temprano = tomarPrefetch<any[]>('vehicles', claveInventario(clientId, hasDemo));
      if (temprano) {
        try {
          const filas = await temprano;
          if (Array.isArray(filas)) data = filas;
        } catch (e) {
          console.warn('Prefetch temprano del inventario falló; se pide de nuevo:', e);
        }
      }

      if (!data) {
        let query = supabase.from('vehicles').select(VEHICLE_INVENTORY_SELECT);

        // Filtrado por cliente
        if (hasDemo) {
          // Si has_demo es true, obtenemos vehículos del cliente y del cliente_id = 1
          query = query.or(`client_id.eq.${clientId},client_id.eq.1`);
        } else {
          // Solo obtenemos los vehículos del cliente
          query = query.eq('client_id', clientId);
        }

        // Ticket #164: el orden INICIAL lo elige la automotora
        // (`clients.web_vehicle_order`) y sale del módulo único
        // `lib/orden-vehiculos`, que comparten esta consulta, la precarga del
        // `<head>` y los comparadores de las grillas. Con el default
        // ('newest') es `created_at desc`, exactamente lo de siempre.
        for (const { column, ascending } of ordenInventario(orden)) {
          query = query.order(column, { ascending });
        }

        const { data: filas, error } = await query;
        if (error) throw error;
        data = filas;
      }

      // Fecha del evento (venta/reserva, con fallback a state_updated_at /
      // updated_at) para Vendido/Reservado
      const processedData: InventoryRow[] =
        data?.map((vehicle: any) => ({
          ...vehicle,
          event_date: latestEventDate(vehicle),
        })) || [];

      // Filtrar en memoria: estados visibles y, para Vendido/Reservado, la
      // ventana configurada en clients_vehicles_states.web_visibility_days
      // (3 días si el tenant no la configuró).
      // Use show_in_web field if defined, fallback to name-based logic for backward compatibility
      const filteredVehicles = processedData.filter((vehicle) => {
        if (!vehicle.status) return false;

        // Determine if vehicle should be shown based on show_in_web or fallback to names
        let shouldShow = false;
        if (typeof vehicle.status.show_in_web === 'boolean') {
          shouldShow = vehicle.status.show_in_web;
        } else {
          // Fallback: use name-based filtering for legacy states without show_in_web
          shouldShow = ['Publicado', 'Reservado', 'Vendido'].includes(vehicle.status.name || '');
        }

        if (!shouldShow) return false;

        return passesRecencyWindow(vehicle);
      });

      console.log('Vehículos filtrados:', filteredVehicles.length);
      set({
        vehicles: filteredVehicles,
        rawVehicles: processedData,
        loadedForClientId: String(clientId),
        error: null,
      });
    } catch (error) {
      console.error('Error en fetchVehicles:', error);
      // Se marca como cargado (vacío) igual: los consumidores pasan a su
      // estado "sin vehículos", que es lo que hacían cuando su propia
      // consulta fallaba, en vez de quedarse esperando para siempre.
      set({ error: (error as Error).message, vehicles: [], rawVehicles: [], loadedForClientId: String(clientId) });
    } finally {
      set({ isLoading: false });
    }
  },
}));

export default useVehiclesStore;
